import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ATTENDANCE_STATUS_MAP,
  EMPLOYEE_STATUS_MAP,
  LEAVE_STATUS_MAP,
  LEAVE_TYPE_DEFS,
  LEAVE_TYPE_MAP,
  type AttendanceStatus,
  type EmployeeStatus,
  type LeaveStatus,
  type LeaveType,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { date, money, num } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, Modal, Notice, PageHead } from '../../components/ui';
import { Fact, FactGrid } from '../../components/staff';
import { useToast } from '../../components/Toast';

/* ==========================================================================
   بوّابة الموظّف — ما يخصّني أنا
   ==========================================================================

   وحدةُ الموارد البشرية كلُّها للإدارة، وهو قرارٌ صحيح: ملفّاتُها تحمل
   رواتبَ الناس وأرقامَهم القومية. فمن أراد أرقامَه هو فله بابٌ آخر لا يقبل
   معرّفَ موظّفٍ في أيّ مسار — يُشتقّ صاحبُ الحساب من الجلسة.
   ========================================================================== */

interface Profile {
  linked: boolean;
  employee: {
    id: string;
    name: string;
    code: string | null;
    jobTitle: string | null;
    department: string | null;
    email: string | null;
    phone: string | null;
    hireDate: string;
    status: EmployeeStatus;
    currency: string;
  } | null;
  manager?: { id: string; name: string; jobTitle: string | null } | null;
  leave?: { balance: number; pendingDays: number; pendingCount: number };
  custodyCount?: number;
}

/** صفحةٌ لمن لا ملفَّ له — تشرح ولا تُلقي خطأً في وجه من لم يخطئ. */
function NotLinked() {
  return (
    <EmptyState
      title="حسابُك غير مربوطٍ بملفّ موظّف"
      text="بياناتُك الوظيفية — إجازاتُك وحضورُك وقسائمُ راتبك — تُقرأ من ملفّك في الموارد البشرية. اطلب منهم ربطَ حسابك بملفّك ليظهر كلُّ ذلك هنا."
    />
  );
}

/* ==========================================================================
   ملفّي
   ========================================================================== */

export function MyProfile() {
  const profile = useQuery({
    queryKey: ['my', 'hr-profile'],
    queryFn: () => api<Profile>('/hr/me/profile'),
  });

  const d = profile.data;

  return (
    <div className="page">
      <PageHead title="ملفّي" subtitle="بياناتُك الوظيفية كما هي في الموارد البشرية" />

      {profile.isError && (
        <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />
      )}
      {profile.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {d && !d.linked && <NotLinked />}

      {d?.linked && d.employee && (
        <>
          <div className="ag-stats">
            <Link to="/my/leaves" className="ag-stat">
              <Icon name="calendar" size={18} />
              <strong className="num">{num(d.leave?.balance ?? 0)}</strong>
              <span>يومَ إجازةٍ متاح</span>
            </Link>
            <Link
              to="/my/leaves"
              className={`ag-stat${(d.leave?.pendingCount ?? 0) > 0 ? ' is-alert' : ''}`}
            >
              <Icon name="clock" size={18} />
              <strong className="num">{d.leave?.pendingCount ?? 0}</strong>
              <span>طلبًا لم يُبتّ فيه</span>
            </Link>
            <Link to="/my/payslips" className="ag-stat">
              <Icon name="wallet" size={18} />
              <strong className="num">—</strong>
              <span>قسائم راتبي</span>
            </Link>
            <Link to="/my/custody" className="ag-stat">
              <Icon name="briefcase" size={18} />
              <strong className="num">{d.custodyCount ?? 0}</strong>
              <span>صنفًا في عهدتي</span>
            </Link>
          </div>

          <div className="card">
            <div className="card-head">
              <h2 className="card-title">بياناتي</h2>
              <Badge kind={EMPLOYEE_STATUS_MAP[d.employee.status]?.kind}>
                {EMPLOYEE_STATUS_MAP[d.employee.status]?.label}
              </Badge>
            </div>
            <div className="card-body">
              <FactGrid>
                <Fact icon="user" label="الاسم" value={d.employee.name} />
                <Fact icon="briefcase" label="المسمّى" value={d.employee.jobTitle || '—'} />
                <Fact icon="building" label="القسم" value={d.employee.department || '—'} />
                <Fact icon="clipboard" label="الكود" value={d.employee.code || '—'} ltr />
                <Fact icon="calendar" label="تاريخ التعيين" value={date(d.employee.hireDate)} />
                <Fact icon="users" label="مديري" value={d.manager?.name || '—'} />
                <Fact icon="mail" label="البريد" value={d.employee.email || '—'} ltr />
                <Fact icon="phone" label="الهاتف" value={d.employee.phone || '—'} ltr />
              </FactGrid>
              <p className="tiny mute mt-4">
                تصحيحُ أيٍّ من هذه البيانات يمرّ على الموارد البشرية — لا تُعدَّل من هنا لأنّها
                تدخل في المرتّب والتأمين.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ==========================================================================
   إجازاتي
   ========================================================================== */

interface MyLeaves {
  balance: number;
  pendingDays: number;
  rows: {
    id: string;
    type: LeaveType;
    typeLabel: string;
    paid: boolean;
    status: LeaveStatus;
    fromDate: string;
    toDate: string;
    days: number;
    reason: string | null;
    decidedAt: string | null;
    createdAt: string;
  }[];
}

export function MyLeaves() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [asking, setAsking] = useState(false);

  const leaves = useQuery({
    queryKey: ['my', 'hr-leaves'],
    queryFn: () => api<MyLeaves>('/hr/me/leaves'),
  });

  const withdraw = useMutation({
    mutationFn: (id: string) => api(`/hr/me/leaves/${id}/withdraw`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my'] });
      toast('سُحب الطلب');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر السحب.'),
  });

  const d = leaves.data;

  return (
    <div className="page">
      <PageHead
        title="إجازاتي"
        subtitle="رصيدُك وطلباتُك وأين وصل كلُّ طلب"
        actions={
          <button className="btn btn-primary" onClick={() => setAsking(true)}>
            <Icon name="plus" size={15} />
            طلب إجازة
          </button>
        }
      />

      {leaves.isError && <ErrorState error={leaves.error} onRetry={() => void leaves.refetch()} />}
      {leaves.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {d && (
        <>
          {/*
            رقمان لا واحد: المتاحُ اليوم، والمحجوزُ في طلباتٍ لم تُبتّ.
            من يقرأ رصيدًا واحدًا وله طلبٌ معلَّقٌ يظنّ أنّ له ما ليس له.
          */}
          <div className="ag-stats">
            <span className="ag-stat">
              <Icon name="calendar" size={18} />
              <strong className="num">{num(d.balance)}</strong>
              <span>يومًا متاحًا الآن</span>
            </span>
            <span className={`ag-stat${d.pendingDays > 0 ? ' is-alert' : ''}`}>
              <Icon name="clock" size={18} />
              <strong className="num">{num(d.pendingDays)}</strong>
              <span>يومًا محجوزًا في طلباتٍ معلّقة</span>
            </span>
          </div>

          {d.pendingDays > 0 && (
            <Notice kind="info" icon="info" title="المحجوزُ لم يُخصم بعد">
              الرصيدُ يُخصم لحظةَ الاعتماد لا لحظةَ الطلب — فإن اعتُمدت طلباتُك المعلّقة صار
              المتاحُ <b className="num">{num(d.balance - d.pendingDays)}</b> يومًا.
            </Notice>
          )}

          {d.rows.length === 0 ? (
            <EmptyState title="لا طلبات بعد" text="اطلب إجازتك من الزرّ أعلى الصفحة." />
          ) : (
            <div className="card">
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>النوع</th>
                      <th>من</th>
                      <th>إلى</th>
                      <th className="col-num">الأيام</th>
                      <th>الحالة</th>
                      <th>السبب</th>
                      <th className="col-act" />
                    </tr>
                  </thead>
                  <tbody>
                    {d.rows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <span className="cell-strong">{r.typeLabel}</span>
                          {!r.paid && <div className="tiny mute">بلا أجر — يُخصم من الراتب</div>}
                        </td>
                        <td className="num cell-mute">{date(r.fromDate)}</td>
                        <td className="num cell-mute">{date(r.toDate)}</td>
                        <td className="num">{num(r.days)}</td>
                        <td>
                          <Badge kind={LEAVE_STATUS_MAP[r.status]?.kind}>
                            {LEAVE_STATUS_MAP[r.status]?.label}
                          </Badge>
                        </td>
                        <td className="cell-mute tiny">{r.reason || '—'}</td>
                        <td className="col-act">
                          {r.status === 'PENDING' && (
                            <button
                              className="btn btn-sm"
                              onClick={() => {
                                if (window.confirm('سحبُ هذا الطلب؟')) withdraw.mutate(r.id);
                              }}
                            >
                              سحب
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {asking && (
        <AskLeaveModal
          onClose={() => setAsking(false)}
          onDone={() => {
            setAsking(false);
            void queryClient.invalidateQueries({ queryKey: ['my'] });
          }}
        />
      )}
    </div>
  );
}

function AskLeaveModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const [type, setType] = useState<LeaveType>('ANNUAL');
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [reason, setReason] = useState('');

  const save = useMutation({
    mutationFn: () =>
      api('/hr/me/leaves', {
        method: 'POST',
        body: { type, fromDate, toDate, reason: reason.trim() || undefined },
      }),
    onSuccess: () => {
      toast('أُرسل الطلب إلى مديرك');
      onDone();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإرسال.'),
  });

  return (
    <Modal title="طلب إجازة" onClose={onClose}>
      <div className="field">
        <span className="label">النوع</span>
        <select
          className="input"
          value={type}
          onChange={(e) => setType(e.target.value as LeaveType)}
        >
          {LEAVE_TYPE_DEFS.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>
        <span className="tiny mute">
          {LEAVE_TYPE_MAP[type].deductsBalance
            ? 'تُخصم من رصيدك السنويّ عند الاعتماد.'
            : 'لا تُخصم من الرصيد السنويّ.'}
          {!LEAVE_TYPE_MAP[type].paid && ' وتُخصم أيامُها من الراتب.'}
        </span>
      </div>

      <div className="grid-2">
        <label className="field">
          <span className="label">من</span>
          <input
            className="input num"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="label">إلى</span>
          <input
            className="input num"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </label>
      </div>

      <label className="field">
        <span className="label">السبب (اختياري)</span>
        <textarea
          className="textarea"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>

      <p className="tiny mute">
        تُحسب أيامُ العمل وحدَها: العطلةُ الأسبوعية والأعيادُ الرسمية لا تُعدّ من إجازتك.
      </p>

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button
          className="btn btn-primary"
          onClick={() => save.mutate()}
          disabled={save.isPending || fromDate > toDate}
        >
          {save.isPending ? 'جارٍ الإرسال…' : 'أرسل الطلب'}
        </button>
        <button className="btn" onClick={onClose}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   حضوري
   ========================================================================== */

interface MyAttendance {
  month: string;
  monthLabel: string;
  totals: {
    present: number;
    absent: number;
    leave: number;
    late: number;
    lateMinutes: number;
    overtimeMinutes: number;
  };
  rows: {
    date: string;
    status: AttendanceStatus;
    checkIn: string | null;
    checkOut: string | null;
    lateMinutes: number;
    overtimeMinutes: number;
    notes: string | null;
  }[];
}

export function MyAttendance() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const data = useQuery({
    queryKey: ['my', 'hr-attendance', month],
    queryFn: () => api<MyAttendance>(`/hr/me/attendance${qs({ month })}`),
    placeholderData: (prev) => prev,
  });

  const shift = (delta: number) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y!, m! - 1 + delta, 1));
    setMonth(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  };

  const d = data.data;

  return (
    <div className="page">
      <PageHead
        title="حضوري"
        subtitle="ما سُجِّل لك شهرًا بشهر"
        actions={
          <span className="seg" role="group" aria-label="الشهر">
            <button type="button" className="seg-btn" onClick={() => shift(-1)}>
              <Icon name="chevronRight" size={13} />
            </button>
            <span className="seg-btn is-on num">{d?.monthLabel ?? month}</span>
            <button type="button" className="seg-btn" onClick={() => shift(1)}>
              <Icon name="chevronLeft" size={13} />
            </button>
          </span>
        }
      />

      {data.isError && <ErrorState error={data.error} onRetry={() => void data.refetch()} />}
      {data.isPending && !d && <div className="skeleton" style={{ height: 200 }} />}

      {d && (
        <>
          <div className="ag-stats">
            <span className="ag-stat">
              <Icon name="check" size={18} />
              <strong className="num">{d.totals.present}</strong>
              <span>يوم حضور</span>
            </span>
            <span className={`ag-stat${d.totals.absent > 0 ? ' is-alert' : ''}`}>
              <Icon name="alert" size={18} />
              <strong className="num">{d.totals.absent}</strong>
              <span>غياب</span>
            </span>
            <span className="ag-stat">
              <Icon name="calendar" size={18} />
              <strong className="num">{d.totals.leave}</strong>
              <span>إجازة</span>
            </span>
            <span className="ag-stat">
              <Icon name="clock" size={18} />
              <strong className="num">{d.totals.lateMinutes}</strong>
              <span>دقيقة تأخير</span>
            </span>
          </div>

          {/*
            الحدُّ الصادق: صفُّ الحضور لا يُخلَق إلّا حين يحفظ المسؤولُ ورقةَ
            اليوم — فاليومُ الغائبُ من الجدول «لم يُسجَّل» لا «غائب».
          */}
          <Notice kind="info" icon="info" title="اليومُ الذي لا تجده هنا لم يُسجَّل بعد">
            ورقةُ الحضور يحفظها المسؤولُ يومًا بيوم، واليومُ الذي لم يُحفظ لا يظهر — ولا يعني
            ذلك غيابًا. راجع مديرك إن رأيت يومًا ناقصًا أو حالةً لا تطابق ما حدث.
          </Notice>

          {d.rows.length === 0 ? (
            <EmptyState title="لا أيام مسجَّلة في هذا الشهر" text="لم تُحفظ ورقةُ حضورٍ بعد." />
          ) : (
            <div className="card">
              <div className="table-wrap sticky-head">
                <table className="table">
                  <thead>
                    <tr>
                      <th>اليوم</th>
                      <th>الحالة</th>
                      <th>الحضور</th>
                      <th>الانصراف</th>
                      <th className="col-num">تأخير</th>
                      <th className="col-num">إضافي</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.rows.map((r) => (
                      <tr key={r.date}>
                        <td className="num cell-mute">{date(r.date)}</td>
                        <td>
                          <Badge kind={ATTENDANCE_STATUS_MAP[r.status]?.kind}>
                            {ATTENDANCE_STATUS_MAP[r.status]?.label ?? r.status}
                          </Badge>
                        </td>
                        <td className="num cell-mute">
                          {r.checkIn ? new Date(r.checkIn).toTimeString().slice(0, 5) : '—'}
                        </td>
                        <td className="num cell-mute">
                          {r.checkOut ? new Date(r.checkOut).toTimeString().slice(0, 5) : '—'}
                        </td>
                        <td className={`num${r.lateMinutes > 0 ? ' ag-late-text' : ' cell-mute'}`}>
                          {r.lateMinutes || '—'}
                        </td>
                        <td className="num cell-mute">{r.overtimeMinutes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ==========================================================================
   رواتبي
   ========================================================================== */

interface PayslipRow {
  id: string;
  period: string;
  periodLabel: string;
  runStatus: string;
  currency: string;
  baseSalary: number;
  additions: number;
  deductions: number;
  netPay: number;
}

export function MyPayslips() {
  const [openSlip, setOpenSlip] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['my', 'hr-payslips'],
    queryFn: () => api<PayslipRow[]>('/hr/me/payslips'),
  });

  const rows = list.data ?? [];
  const paid = rows.filter((r) => r.runStatus === 'PAID');
  const total = paid.reduce((s, r) => s + r.netPay, 0);

  return (
    <div className="page">
      <PageHead title="رواتبي" subtitle="قسائمُ راتبك المعتمَدة — كما صُرفت لا كما تُحسب اليوم" />

      {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}
      {list.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا قسائم بعد"
          text="تظهر قسيمةُ الشهر هنا بعد أن تعتمد الإدارةُ دورةَ مرتّبه. ودورةٌ لم تُعتمد لا تُعرض لأنّها قد يُعاد حسابُها."
        />
      )}

      {rows.length > 0 && (
        <>
          <div className="ag-stats">
            <span className="ag-stat">
              <Icon name="wallet" size={18} />
              <strong className="num">{money(rows[0]!.netPay)}</strong>
              <span>صافي آخر قسيمة</span>
            </span>
            <span className="ag-stat">
              <Icon name="check" size={18} />
              <strong className="num">{paid.length}</strong>
              <span>قسيمة مصروفة</span>
            </span>
            <span className="ag-stat">
              <Icon name="chart" size={18} />
              <strong className="num">{money(total)}</strong>
              <span>مجموع ما صُرف</span>
            </span>
          </div>

          <div className="card">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>الشهر</th>
                    <th className="col-num">الأساسي</th>
                    <th className="col-num">الإضافات</th>
                    <th className="col-num">الخصومات</th>
                    <th className="col-num">الصافي</th>
                    <th>الحالة</th>
                    <th className="col-act" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="clickable" onClick={() => setOpenSlip(r.id)}>
                      <td className="cell-strong">{r.periodLabel}</td>
                      <td className="num cell-mute">{money(r.baseSalary)}</td>
                      <td className="num cell-mute">{money(r.additions)}</td>
                      <td className="num cell-mute">{money(r.deductions)}</td>
                      <td className="num cell-strong">{money(r.netPay)}</td>
                      <td>
                        <Badge kind={r.runStatus === 'PAID' ? 'success' : 'info'}>
                          {r.runStatus === 'PAID' ? 'صُرفت' : 'معتمدة'}
                        </Badge>
                      </td>
                      <td className="col-act">
                        <Icon name="chevronLeft" size={14} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {openSlip && <PayslipModal id={openSlip} onClose={() => setOpenSlip(null)} />}
    </div>
  );
}

interface PayslipDetail extends PayslipRow {
  employee: { name: string; code: string | null; jobTitle: string | null; department: string | null };
  presentDays: number;
  absentDays: number;
  leaveDays: number;
  breakdown: { kind: string; label: string; amount: number; note?: string }[];
}

/**
 * تفصيلُ القسيمة.
 *
 * الأرقامُ مجمَّدةٌ لحظةَ إنشاء الدورة ولا يُعاد حسابُها عند العرض: ما رآه
 * الموظّفُ أمسِ هو ما قبضه، ولو رُفع مرتّبُه اليوم.
 */
function PayslipModal({ id, onClose }: { id: string; onClose: () => void }) {
  const slip = useQuery({
    queryKey: ['my', 'hr-payslip', id],
    queryFn: () => api<PayslipDetail>(`/hr/me/payslips/${id}`),
  });

  const d = slip.data;

  return (
    <Modal title={d ? `قسيمة ${d.periodLabel}` : 'القسيمة'} onClose={onClose} wide>
      {slip.isPending && <div className="skeleton" style={{ height: 220 }} />}
      {slip.isError && <ErrorState error={slip.error} onRetry={() => void slip.refetch()} />}

      {d && (
        <>
          <FactGrid>
            <Fact icon="user" label="الموظف" value={d.employee.name} />
            <Fact icon="briefcase" label="المسمّى" value={d.employee.jobTitle || '—'} />
            <Fact icon="check" label="أيام الحضور" value={num(d.presentDays)} />
            <Fact icon="alert" label="أيام الغياب" value={num(d.absentDays)} />
          </FactGrid>

          <div className="table-wrap mt-4">
            <table className="table">
              <thead>
                <tr>
                  <th>البند</th>
                  <th>التفصيل</th>
                  <th className="col-num">المبلغ</th>
                </tr>
              </thead>
              <tbody>
                {(d.breakdown ?? []).map((l, i) => (
                  <tr key={i}>
                    <td className="cell-strong">{l.label}</td>
                    <td className="cell-mute tiny">{l.note ?? '—'}</td>
                    <td className={`num${l.amount < 0 ? ' ag-late-text' : ''}`}>
                      {money(l.amount)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="cell-strong">الصافي</td>
                  <td />
                  <td className="num cell-strong">{money(d.netPay)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="tiny mute mt-4">
            هذه الأرقامُ محسوبةٌ لحظةَ إقفال دورة {d.periodLabel} ومجمَّدة — لا تتغيّر بتغيّر
            مرتّبك بعدها. وأيُّ اعتراضٍ يُراجَع مع الموارد البشرية.
          </p>
        </>
      )}
    </Modal>
  );
}

/* ==========================================================================
   عهدتي
   ========================================================================== */

export function MyCustody() {
  const list = useQuery({
    queryKey: ['my', 'hr-custody'],
    queryFn: () => api<Record<string, unknown>[]>('/hr/me/custody'),
  });

  const rows = list.data ?? [];

  return (
    <div className="page">
      <PageHead title="عهدتي" subtitle="ما تسلّمتَه من أصول الشركة، ومتى" />

      {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState title="لا عهدة عليك" text="ما تتسلّمه من أجهزةٍ أو أدواتٍ يظهر هنا باسمه وتاريخه." />
      )}

      {rows.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>الكود</th>
                  <th>الصنف</th>
                  <th>من تاريخ</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={String(a.id)}>
                    <td className="num cell-mute">{String(a.code ?? '—')}</td>
                    <td className="cell-strong">{String(a.name ?? '—')}</td>
                    <td className="num cell-mute">
                      {a.custodyFrom ? date(String(a.custodyFrom)) : '—'}
                    </td>
                    <td className="cell-mute tiny">{String(a.status ?? '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="tiny mute" style={{ padding: 'var(--sp-3)' }}>
            تسليمُ العهدة واستردادُها يُسجَّلان في وحدة الأصول — وإنهاءُ خدمتك يُمنع حتى تُسلَّم.
          </p>
        </div>
      )}
    </div>
  );
}
